import {
  FormCheckbox,
  FormDatePicker,
  FormInput,
  FormSelect,
  FormTextarea,
  Heading,
  Loader,
} from "@/components";
import useAddEdit from "./useAddEdit";
import { FormProvider } from "react-hook-form";
import {
  DESCRIPTION,
  YEAR,
  BILLING_CATEGORY,
  TRANSACTION_TYPE,
  AUTO_BILLING,
  ACCOUNT_NUMBER,
  FREQUENCY,
  AMOUNT,
  INTEREST,
  BILLING_DATE,
  COMMUNITY_ID,
  CURRENCY,
  PAYMENT_TERM,
  MONTHLY_BILLING_DATE,
  PROPERTY_IDS,
  DUE_DATE,
  MONTHLY_POSTING_DATE,
  POSTING_DATE,
} from "./consts";
import { Button } from "@/components/ui";
import {
  frequencyOptions,
  frequencyTypeOption,
  transactionTypeOptions,
} from "../../consts";
import { allOption } from "@/lib/utils";

const AddEdit = () => {
  const {
    breadcrumbData,
    onBackClick,
    form,
    onSubmit,
    requiredFields,
    isAddPending,
    onCancel,
    billingCategory,
    isLoadingBillingCategory,
    glData,
    isLoadingGlMaster,
    keys,
    communityDetail,
    paymentTerms,
    isPaymentTermLoading,
    isView,
    isBillingConfigLoading,
    isJobPending,
    isJobExecuted,
    onRunJob,
    billingConfigData,
    propertyByModel,
    onBillingCategoryChange,
    isDocumentTypeLoading
  } = useAddEdit();
  const { errors } = form.formState;

  return (
    <div className="p-4 md:p-6">
      <Heading
        title={`${isView ? "View" : "Add"} Billing Configuration - ${communityDetail?.name
          }`}
        showBack
        breadcrumbData={breadcrumbData}
        onBackClick={onBackClick}
      />

      {isLoadingBillingCategory ||
        isLoadingGlMaster ||
        isPaymentTermLoading ||
        isBillingConfigLoading ||
        isDocumentTypeLoading ? (
        <Loader />
      ) : (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-[30px]">
                <FormInput
                  name={COMMUNITY_ID}
                  control={form.control}
                  label="Community ID"
                  placeholder="Please enter"
                  error={errors[COMMUNITY_ID]?.message}
                  isRequired={requiredFields.includes(COMMUNITY_ID)}
                  disabled
                />
                <FormInput
                  name={YEAR}
                  control={form.control}
                  label="Year"
                  placeholder="Please enter"
                  error={errors[YEAR]?.message}
                  isRequired={requiredFields.includes(YEAR)}
                  disabled
                />
                <FormSelect
                  key={keys.transactionType}
                  name={TRANSACTION_TYPE}
                  control={form.control}
                  label="Transaction Type"
                  placeholder="Please select"
                  error={errors[TRANSACTION_TYPE]?.message}
                  isRequired
                  options={transactionTypeOptions}
                  onChange={(val: any) => {
                    form.setValue(FREQUENCY, (frequencyTypeOption as any)[val]);
                    form.setValue(BILLING_DATE, "");
                    form.clearErrors(FREQUENCY);

                    if ((frequencyTypeOption as any)[val] == "AD_HOC") {
                      form.setValue(AUTO_BILLING, false);
                    } else {
                      form.setValue(AUTO_BILLING, true);
                    }
                  }}
                  disabled={isView}
                />
                <FormSelect
                  multiSelect
                  key={keys.billingCategory}
                  name={BILLING_CATEGORY}
                  control={form.control}
                  label="Billing Category"
                  placeholder="Please select"
                  error={errors[BILLING_CATEGORY]?.message}
                  isRequired={form.watch(FREQUENCY) == "AD_HOC"}
                  options={[...allOption, ...(billingCategory?.data || [])
                    ?.map((data: any) => ({
                      value: data?.id?.toString(),
                      label: data?.name,
                    }))]}
                  onChange={onBillingCategoryChange}
                  disabled={isView}
                />
                {form.watch(BILLING_CATEGORY) &&
                form.watch(TRANSACTION_TYPE) == "ADHOC_HOA_FEE" ? (
                  <FormSelect
                    key={keys.propertyModel}
                    name={PROPERTY_IDS}
                    control={form.control}
                    label="Properties"
                    placeholder="Please select"
                    error={errors[PROPERTY_IDS]?.message}
                    options={[
                      { value: "all", label: "All Properties" },
                      ...(propertyByModel?.data || []).map((data: any) => ({
                        value: data?.propertyId?.toString(),
                        label: data?.name,
                      })),
                    ]}
                    disabled={isView || !form.watch(BILLING_CATEGORY)}
                    multiSelect
                  />
                ) : (
                  <></>
                )}
                <FormCheckbox
                  name={AUTO_BILLING}
                  control={form.control}
                  label="Auto Billing"
                  placeholder=""
                  error={errors[AUTO_BILLING]?.message}
                  isRequired={requiredFields.includes(AUTO_BILLING)}
                  disabled
                />
                <FormSelect
                  key={keys.accountName}
                  name={ACCOUNT_NUMBER}
                  control={form.control}
                  label="Account Number"
                  placeholder="Please select"
                  error={errors[ACCOUNT_NUMBER]?.message}
                  isRequired
                  options={(glData?.data || [])
                    ?.filter((data: any) => data?.accountCategory == "I" && data?.active === true)
                    ?.sort((a: any, b: any) =>
                      a.glAccountNumber?.localeCompare(b.glAccountNumber)
                    )
                    ?.map((data: any) => {
                      return {
                        label: `${data?.glAccountNumber} - ${data?.accountShortDesc}`,
                        value: data?.glMasterId?.toString(),
                      };
                    })}
                  disabled={isView}
                />
                <FormSelect
                  key={keys.freq}
                  name={FREQUENCY}
                  control={form.control}
                  label="Frequency"
                  placeholder="Please select"
                  error={errors[FREQUENCY]?.message}
                  isRequired
                  options={frequencyOptions}
                  onChange={() => form.setValue(BILLING_DATE, "")}
                  disabled
                />
                <FormInput
                  name={AMOUNT}
                  control={form.control}
                  label="Amount"
                  placeholder="Please enter"
                  error={errors[AMOUNT]?.message}
                  isRequired
                  startIcon="$"
                  variant="integerSpeChar"
                  disabled={isView}
                />
                <FormInput
                  name={CURRENCY}
                  control={form.control}
                  label="Currency"
                  placeholder="Please enter"
                  error={errors[CURRENCY]?.message}
                  isRequired={requiredFields.includes(CURRENCY)}
                  disabled
                />
                <FormInput
                  name={INTEREST}
                  control={form.control}
                  label="Interest %"
                  placeholder="Please enter"
                  error={errors[INTEREST]?.message}
                  isRequired={requiredFields.includes(INTEREST)}
                  variant="integer"
                  disabled={isView}
                />
                {form.watch(FREQUENCY) == "MONTHLY" && (
                  <FormSelect
                    key={keys.paymentTerm}
                    name={PAYMENT_TERM}
                    control={form.control}
                    label="Payment Term"
                    placeholder="Please select"
                    error={errors[PAYMENT_TERM]?.message}
                    isRequired
                    options={(paymentTerms?.data || [])
                      ?.map((data: any) => ({
                        value: data?.id?.toString(),
                        label: data?.name,
                      }))}
                    disabled={isView}
                  />
                )}
                {form.watch(FREQUENCY) != "MONTHLY" ? (
                  <>
                    <div>
                      <FormDatePicker
                        name={BILLING_DATE}
                        control={form.control}
                        label="Billing Date"
                        placeholder="Please select"
                        error={errors[BILLING_DATE]?.message}
                        isRequired
                        onClear={() => form.setValue(BILLING_DATE, "")}
                        minDate={new Date()}
                        disabled={isView}
                      />
                    </div>
                    {(form.watch(FREQUENCY) === "ANNUAL" ||
                      form.watch(FREQUENCY) === "AD_HOC") && (
                        <div>
                          <FormDatePicker
                            name={POSTING_DATE}
                            control={form.control}
                            label="Posting Date"
                            placeholder="Please select"
                            error={errors[POSTING_DATE]?.message}
                            isRequired
                            onClear={() => form.setValue(POSTING_DATE, "")}
                            minDate={new Date()}
                            disabled={isView}
                          />
                        </div>
                      )}
                    <div>
                      <FormDatePicker
                        name={DUE_DATE}
                        control={form.control}
                        label="Due Date"
                        placeholder="Please select"
                        error={errors[DUE_DATE]?.message}
                        isRequired
                        onClear={() => form.setValue(DUE_DATE, "")}
                        minDate={new Date()}
                        disabled={isView}
                      />
                    </div>
                  </>
                ) : form.watch(FREQUENCY) == "MONTHLY" ? (
                  <>
                    <div>
                      <FormSelect
                        name={MONTHLY_BILLING_DATE}
                        control={form.control}
                        label="Billing Day"
                        placeholder="Please select"
                        error={errors[MONTHLY_BILLING_DATE]?.message}
                        isRequired
                        options={new Array(31)
                          .fill(0)
                          .map((_, index: number) => ({
                            label: (index + 1)?.toString(),
                            value: (index + 1)?.toString(),
                          }))}
                        disabled={isView}
                      />
                    </div>
                    <div>
                      <FormSelect
                        name={MONTHLY_POSTING_DATE}
                        control={form.control}
                        label="Posting Day"
                        placeholder="Please select"
                        error={errors[MONTHLY_POSTING_DATE]?.message}
                        isRequired
                        options={[
                          ...new Array(28).fill(0).map((_, index: number) => ({
                            label: (index + 1)?.toString(),
                            value: (index + 1)?.toString(),
                          })),
                          {
                            label: "last day of month",
                            value: "last day of month",
                          },
                        ]}
                        disabled={isView}
                      />
                    </div>
                    {/* <div>
                      <FormDatePicker
                        name={DUE_DATE}
                        control={form.control}
                        label="Due Date"
                        placeholder="Please select"
                        error={errors[DUE_DATE]?.message}
                        isRequired
                        onClear={() => form.setValue(DUE_DATE, "")}
                        minDate={new Date()}
                        disabled={isView}
                      />
                    </div> */}
                  </>
                ) : (
                  <></>
                )}
                <FormTextarea
                  name={DESCRIPTION}
                  control={form.control}
                  label="Description"
                  placeholder="Please enter"
                  error={errors[DESCRIPTION]?.message}
                  isRequired={requiredFields.includes(DESCRIPTION)}
                  disabled={isView}
                />
              </div>
            </div>
            <div className="flex flex-row justify-end gap-3  py-3">
              <Button
                variant="outline"
                type="button"
                onClick={onCancel}
                disabled={isAddPending}
              >
                Cancel
              </Button>
              {isView ? (
                billingConfigData?.data?.active &&
                  !billingConfigData?.data?.lastBillingDate &&
                  form.watch(FREQUENCY) == "AD_HOC" ? (
                  <Button
                    type="button"
                    disabled={isJobPending || isJobExecuted}
                    onClick={onRunJob}
                  >
                    {isJobPending && <Loader variant="secondary" />}
                    Run Job
                  </Button>
                ) : (
                  <></>
                )
              ) : (
                <Button type="submit" disabled={isAddPending}>
                  {isAddPending && <Loader variant="secondary" />}
                  Save
                </Button>
              )}
            </div>
          </form>
        </FormProvider>
      )}
    </div>
  );
};

export default AddEdit;
